/**
 * ticket-inapp.strata — the second of the package's two shipped in-app
 * card variants (see ticket-inapp.card.ts for the variant dispatcher). The
 * card splits into two horizontal bands — identity above, credential below
 * — separated by surface tone alone, with the identity band inverted to
 * near-black. The credential band stays the brightest surface in the whole
 * composition, which is also the functionally correct choice for a QR:
 * maximum light-on-dark contrast at the door scanner. Both bands hang off
 * one shared left content edge — nothing on the card centers. Mounts the
 * real QR hero (ticket-inapp.qr-hero.ts) in its `fill` shape, sized by the
 * height the band's trailing lines leave rather than a fixed pixel edge —
 * see that file's header for why `fill` carries no white plate and the
 * band's own bright surface serves as the quiet zone.
 *
 * ONE MECHANISM FOR BOTH BANDS: each paints a pinned ground —
 * `tkt-ink-ground` for the identity band, `tkt-paper-ground` for the
 * credential band (ticket-inapp.palette.ts) — never a theme surface family,
 * so the split survives a light host unchanged and the credential band stays
 * the bright one the QR's black modules need. A `<schmancy-surface>` would
 * invert with the ambient scheme and additionally let the host page bleed
 * through its translucent fill, which is the opposite of what a document
 * wants. The band's own `<schmancy-divider>` draws through the paper ground's
 * pinned `outlineVariant`, so the rule holds its tone with the band.
 *
 * A hairline `border-outlineVariant` rides the outer card only (never the
 * internal seam, which stays tone-only) — the identity band's near-black
 * fill is visually identical to whatever dark page this card sits on with no
 * perimeter edge, and that outer edge is the one thing on the card whose job
 * genuinely depends on the host, so it keeps tracking the ambient `outline`
 * family and reads correctly against a dark OR light host page.
 */

import { formatDateRange } from "@mhmo91/rx";
import { translate } from "@momo/i18n";
import { html, type TemplateResult } from "lit";
import type { TicketTemplateProps } from "./ticket.props.ts";
import { ticketCardGrounds } from "./ticket-inapp.palette.ts";
import { renderQrCredentialHero } from "./ticket-inapp.qr-hero.ts";
import { ticketCardStyles } from "./ticket-inapp.styles.ts";

export function renderTicketInAppStrata(
	props: TicketTemplateProps,
): TemplateResult {
	const { locale, brand, event, ticket } = props;
	const ticketTypeLabel = translate(locale, "documents.ticket.ticketTypeLabel");
	const scheduleValue = formatDateRange(event.start, event.end, {
		locale: locale === "de" ? "de-DE" : "en-US",
		month: "long",
		timeZone: event.timezone,
	});

	return html`
    ${ticketCardGrounds}${ticketCardStyles}
    <style>
      /* Co-located <style> for the same reason the hero's own fill block is
         (ticket-inapp.qr-hero.ts): child selectors and a min-height floor
         have no non-arbitrary utility, and an arbitrary utility used only in
         THIS package is never emitted into a consumer app's Tailwind output.
         A shrink-to-fit credential contract: the QR takes the height the
         band's own lines leave it, floored so it stays scannable. */
      .tkt-st-qr {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        /* The band aligns items to start, so a flex item no longer
           stretches; the wrapper claims the band's full content width
           itself so the fill square has a real box to resolve against. */
        width: 100%;
      }
      /* gap tightened from the hero's own gap-3 default: this card stacks
         its whole identity above the credential, so every spared pixel is
         height the QR keeps. */
      .tkt-st-qr .tkt-qr-stack { flex: 1; min-height: 0; gap: 0.5rem; }
      /* width:auto lets the square follow the flex-resolved HEIGHT rather
         than the band's width, so the code grows to the largest square the
         divider/id/brand lines leave and shrinks with them — pinned to the
         band's left content edge, never centered. min-height is the floor
         that keeps it SCANNABLE; past it the band's trailing lines overflow
         into the card's overflow-hidden bottom-first (brand, then id),
         while the QR — first in the column — clips only once the identity
         band above has consumed nearly the whole card. */
      .tkt-st-qr .tkt-qr-fill {
        flex: 1;
        min-height: 5rem;
        width: auto;
        max-width: 100%;
      }
    </style>
    <div class="tkt-card tkt-card--column tkt-card--raised">
      <div class="tkt-ink-ground tkt-st-identity">
        <schmancy-typography type="label" token="xs" transform="uppercase" class="tkt-track-widest tkt-dim">
          ${ticketTypeLabel} — ${ticket.typeName}
        </schmancy-typography>

        <schmancy-typography type="headline" token="md" weight="bold" maxLines="2" class="tkt-st-title">
          ${event.name}
        </schmancy-typography>

        <div class="tkt-st-when">
          <schmancy-typography type="title" token="sm">${scheduleValue}</schmancy-typography>
          <schmancy-typography type="body" token="sm" class="tkt-dim">
            ${event.venue.name} · ${event.venue.address}
          </schmancy-typography>
        </div>
      </div>

      <!-- items-start, nothing centered: every line on this band hangs off
           the same left content edge the identity band's text does. The
           credential absorbs the slack — the chain min-height:0 → flex:1
           (see the <style> block above) hands the QR exactly the height the
           divider/id/brand lines leave, so it FILLS the remaining space and
           shrinks with it rather than riding a fixed pixel size into the
           card's overflow-hidden. -->
      <div class="tkt-paper-ground tkt-st-band">
        <div class="tkt-st-qr">${renderQrCredentialHero(locale, ticket.qrToken, "fill", "start")}</div>

        <schmancy-divider class="tkt-full-width"></schmancy-divider>

        <schmancy-typography type="label" token="sm" class="tkt-mono tkt-track-wide tkt-dim">
          ${ticket.displayId}
        </schmancy-typography>

        <schmancy-typography type="label" token="xs" transform="uppercase" class="tkt-track-widest tkt-dim">
          ${brand.name}
        </schmancy-typography>
      </div>
    </div>
  `;
}
